import React, { useState, useEffect, useCallback } from "react";
import RoleBasedSidebar from "components/ui/RoleBasedSidebar";
import Icon from "components/AppIcon";
import UpdateCard from "./components/UpdateCard";
import NotificationPanel from "./components/NotificationPanel";
import UserProfile from "./components/UserProfile";
import DownloadHistory from "./components/DownloadHistory";
import DownloadModal from "./components/DownloadModal";
import StatsBar from "./components/StatsBar";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const TABS = [
  { id: "updates", label: "Actualizaciones", icon: "Package" },
  { id: "profile", label: "Mi Perfil", icon: "User" },
  { id: "history", label: "Historial", icon: "History" },
];

const CustomerUserDashboard = () => {
  const navigate = useNavigate();
  const { userProfile, signOut, updateProfile } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState("updates");
  const [notifications, setNotifications] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedUpdate, setSelectedUpdate] = useState(null);
  const [filterStatus, setFilterStatus] = useState("todos");
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const orgId = userProfile?.organization_id;

  const fetchAssignments = useCallback(async () => {
    if (!orgId) return;
    try {
      const { data, error } = await supabase?.from('update_assignments')?.select('*, updates(id, name, version, description, file_size, file_name, file_path, created_at)')?.eq('organization_id', orgId)?.eq('status', 'ACTIVE')?.order('created_at', { ascending: false });

      if (error) throw error;

      const now = new Date();
      const mapped = (data || [])?.map(a => {
        const expDate = a?.signed_url_expires_at ? new Date(a.signed_url_expires_at) : null;
        const isExpired = expDate ? expDate < now : false;
        const daysLeft = expDate ? Math.ceil((expDate - now) / (1000 * 60 * 60 * 24)) : null;

        let status = 'disponible';
        if (isExpired) status = 'expirado';
        else if (a?.download_count > 0) status = 'descargado';

        return {
          id: a?.id,
          name: a?.updates?.name || '—',
          version: a?.updates?.version || '—',
          releaseDate: new Date(a.updates?.created_at)?.toLocaleDateString('es-CO'),
          assignedDate: new Date(a.created_at)?.toLocaleDateString('es-CO'),
          description: a?.updates?.description || '',
          fileSize: a?.updates?.file_size ? `${(a?.updates?.file_size / 1024 / 1024)?.toFixed(1)} MB` : '—',
          status,
          expirationDate: a?.signed_url_expires_at,
          daysLeft,
          signedUrl: a?.signed_url,
          isExpired,
          downloadCount: a?.download_count || 0,
          downloadLimit: a?.download_limit,
          filePath: a?.updates?.file_path,
          _assignmentId: a?.id,
          _raw: a,
        };
      });

      setUpdates(mapped);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  const fetchHistory = useCallback(async () => {
    if (!userProfile?.id) return;
    try {
      const { data, error } = await supabase?.from('download_logs')?.select('*, update_assignments(updates(name, version))')?.eq('user_id', userProfile?.id)?.order('downloaded_at', { ascending: false })?.limit(50);

      if (error) throw error;

      const mapped = (data || [])?.map(log => ({
        id: log?.id,
        name: log?.update_assignments?.updates?.name || '—',
        version: log?.update_assignments?.updates?.version || '—',
        downloadedAt: new Date(log.downloaded_at)?.toLocaleString('es-CO'),
        status: 'Completado',
      }));

      setHistory(mapped);
    } catch (err) {
    }
  }, [userProfile?.id]);

  useEffect(() => {
    if (orgId) {
      fetchAssignments();
      fetchHistory();

      // Real-time: new assignments for this org
      const channel = supabase?.channel(`assignments_org_${orgId}`)?.on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'update_assignments',
          filter: `organization_id=eq.${orgId}`,
        }, (payload) => {
          fetchAssignments();
          setUnreadCount(prev => prev + 1);
          setNotifications(prev => [{
            id: Date.now(),
            type: 'asignacion',
            message: 'Nueva actualizaci\u00f3n asignada a su cuenta.',
            time: 'Ahora',
            read: false,
          }, ...prev]);
        })?.subscribe();

      return () => supabase?.removeChannel(channel);
    }
  }, [orgId, fetchAssignments, fetchHistory]);

  const handleDownload = (update) => {
    if (update?.isExpired) {
      return; // UpdateCard handles expired state
    }
    setSelectedUpdate(update);
  };

  const handleRequestRenewal = async (update) => {
    try {
      // Notify admins about renewal request
      const { data: admins } = await supabase
        ?.from('profiles')
        ?.select('email')
        ?.in('role', ['ADMIN', 'SUPER_ADMIN'])
        ?.eq('status', 'ACTIVE');

      const adminEmails = (admins || [])?.map(a => a?.email)?.filter(Boolean);
      if (adminEmails?.length > 0) {
        const { sendEmail } = await import('../../lib/emailService');
        await sendEmail({
          to: adminEmails,
          template: 'admin_nuevo_registro',
          variables: {
            razon_social: userProfile?.organizations?.razon_social || '—',
            nit: userProfile?.organizations?.nit || '—',
            email: userProfile?.email || '',
            fecha: new Date()?.toLocaleString('es-CO'),
            admin_url: `${window?.location?.origin}/update-management`,
          },
        });
      }
      alert(`Solicitud de renovación enviada para "${update?.name}". Un administrador la procesará pronto.`);
    } catch (err) {
    }
  };

  const handleDownloadComplete = async (update) => {
    try {
      // Log download
      await supabase?.from('download_logs')?.insert({
        assignment_id: update?._assignmentId,
        user_id: userProfile?.id,
        organization_id: orgId,
        downloaded_at: new Date()?.toISOString(),
      });

      // Increment download count
      await supabase?.from('update_assignments')?.update({ download_count: (update?.downloadCount || 0) + 1 })?.eq('id', update?._assignmentId);

      await fetchAssignments();
      await fetchHistory();
    } catch (err) {
    }
  };

  const handleSaveProfile = async (form) => {
    await updateProfile({
      full_name: form?.nombre || form?.name,
      phone: form?.telefono || form?.phone,
    });
  };

  const handleMarkRead = (id) => {
    setNotifications(prev => prev?.map(n => n?.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const handleMarkAllRead = () => {
    setNotifications(prev => prev?.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const filteredUpdates = filterStatus === "todos" ? updates : updates?.filter(u => u?.status === filterStatus);

  const stats = {
    total: updates?.length,
    disponibles: updates?.filter(u => u?.status === 'disponible')?.length,
    descargadas: updates?.filter(u => u?.status === 'descargado')?.length,
    porVencer: updates?.filter(u => {
      if (!u?.expirationDate || u?.status === 'expirado') return false;
      const days = Math.ceil((new Date(u.expirationDate) - new Date()) / (1000 * 60 * 60 * 24));
      return days > 0 && days <= 7;
    })?.length,
  };

  const user = {
    nombre: userProfile?.full_name || 'Usuario',
    correo: userProfile?.email || '',
    telefono: userProfile?.phone || '',
    organizacion: userProfile?.organizations?.razon_social || '—',
    nit: userProfile?.organizations?.nit || '—',
    ciudad: '—',
    role: userProfile?.role || 'CUSTOMER_USER',
  };

  const sidebarUser = { name: user?.nombre, role: 'CUSTOMER_USER', organization: user?.organizacion };
  const sidebarNotifications = { availableUpdates: stats?.disponibles };
  const contentMargin = sidebarCollapsed ? "lg:ml-[60px]" : "lg:ml-[240px]";

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--color-background)" }}>
      <RoleBasedSidebar user={sidebarUser} notifications={sidebarNotifications} isCollapsed={sidebarCollapsed} onCollapsedChange={setSidebarCollapsed} onLogout={handleLogout} />
      <main className={`transition-all duration-250 ${contentMargin} pt-16 lg:pt-0`}>
        {/* Vital branded header */}
        <div className="sticky top-0 z-10 px-4 md:px-6 lg:px-8 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" style={{ backgroundColor: 'var(--color-card)', borderBottom: '2px solid var(--color-primary)', boxShadow: '0 2px 8px rgba(0,188,212,0.1)' }}>
          <div className="flex items-center gap-3 pl-10 lg:pl-0">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#00BCD4' }}>
              <Icon name="Monitor" size={18} color="#fff" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)" }}>Mi Dashboard</h1>
              <p className="text-xs" style={{ color: "var(--color-primary)", fontFamily: 'var(--font-caption)' }}>Bienvenido, {user?.nombre?.split(" ")?.[0]} · {user?.organizacion}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button className="w-10 h-10 rounded-lg border flex items-center justify-center transition-colors" style={{ borderColor: "var(--color-primary)", backgroundColor: "var(--color-card)" }} onClick={() => setActiveTab("profile")} title="Notificaciones">
                <Icon name="Bell" size={18} color="var(--color-primary)" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center" style={{ backgroundColor: "#00BCD4", color: "white" }}>{unreadCount}</span>
                )}
              </button>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--color-border)", backgroundColor: "var(--color-card)" }}>
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: "#00BCD4", color: "white", border: '2px solid #B2EBF2' }}>
                {user?.nombre?.split(" ")?.map(n => n?.[0])?.slice(0, 2)?.join("")}
              </div>
              <span className="text-sm font-medium hidden sm:block" style={{ color: "var(--color-foreground)" }}>{user?.nombre?.split(" ")?.[0]}</span>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
          <div className="mb-6"><StatsBar stats={stats} /></div>

          <div className="flex gap-1 mb-6 overflow-x-auto pb-1" style={{ borderBottom: "1px solid var(--color-border)" }}>
            {TABS?.map(tab => (
              <button key={tab?.id} onClick={() => setActiveTab(tab?.id)} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg flex-shrink-0 transition-all`} style={{ fontFamily: "var(--font-caption)", color: activeTab === tab?.id ? "var(--color-primary)" : "var(--color-muted-foreground)", borderBottom: activeTab === tab?.id ? '2px solid var(--color-primary)' : '2px solid transparent', marginBottom: activeTab === tab?.id ? "-1px" : "0", backgroundColor: activeTab === tab?.id ? 'rgba(0,188,212,0.06)' : 'transparent' }}>
                <Icon name={tab?.icon} size={15} color="currentColor" />
                {tab?.label}
              </button>
            ))}
          </div>

          {activeTab === "updates" && (
            <div>
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {["todos", "disponible", "descargado", "expirado"]?.map(s => (
                  <button key={s} onClick={() => setFilterStatus(s)} className="px-3 py-1.5 rounded-full text-xs font-medium transition-all" style={{ fontFamily: "var(--font-caption)", backgroundColor: filterStatus === s ? "#00BCD4" : "var(--color-muted)", color: filterStatus === s ? "#fff" : "var(--color-muted-foreground)" }}>
                    {s?.charAt(0)?.toUpperCase() + s?.slice(1)}
                  </button>
                ))}
              </div>
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 0.8s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                </div>
              ) : filteredUpdates?.length === 0 ? (
                <div className="text-center py-16">
                  <Icon name="Package" size={40} color="var(--color-muted-foreground)" />
                  <p className="mt-3 text-sm" style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)" }}>No hay actualizaciones disponibles</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredUpdates?.map(update => (
                    <UpdateCard key={update?.id} update={update} onDownload={handleDownload} onRequestRenewal={handleRequestRenewal} />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "profile" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2">
                <UserProfile user={user} onSave={handleSaveProfile} />
              </div>
              <div>
                <NotificationPanel notifications={notifications} onMarkRead={handleMarkRead} onMarkAllRead={handleMarkAllRead} />
              </div>
            </div>
          )}

          {activeTab === "history" && (
            <DownloadHistory history={history} />
          )}
        </div>
      </main>
      {selectedUpdate && (
        <DownloadModal 
          update={selectedUpdate} 
          onClose={() => setSelectedUpdate(null)} 
          onComplete={handleDownloadComplete} 
        />
      )}
    </div>
  );
};

export default CustomerUserDashboard;